import { eq } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema, type UserCard } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import {
  persistStudyCardReviewInTransaction,
  type PersistedStudyCardReview,
} from "@-/dev.improve.haniverse.lib.cards.review";
import { REVIEW_RATING } from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import {
  ensureUserCardRows,
  resolveUserCardRowIds,
  startUserCardSchedule,
} from "@-/dev.improve.haniverse.lib.cards.user-card.rows";

/** The `user_card` columns enrolling a quiz card writes. */
type QuizEnrollmentColumns = Pick<
  UserCard,
  | "deferredUntil"
  | "deletedAt"
  | "fsrsCardId"
  | "queuedAt"
  | "reviewRechargeAfter"
  | "startedStudyingAt"
>;

/** A quiz card's rows as they stand, tombstones included. */
export type QuizCardState = {
  /** `null` when the `user_card` row does not exist. */
  card: QuizEnrollmentColumns | null;
  /** `null` when the quiz has no overlay row at all. */
  overlay: { deletedAt: string | null; id: string } | null;
  userCardId: string;
};

/** What a quiz's first answer brought into study, and what its rows held before. */
export type QuizEnrollmentChange = {
  /** `null` when the answer created the card and its overlay. */
  before: { card: QuizEnrollmentColumns | null; overlayDeletedAt: string | null } | null;
  /** The card's scheduler row, which the answer started over. */
  fsrsCardId: string;
  overlayId: string;
  /** Tells this enrollment apart from a later one: a card keeps one scheduler row for good. */
  startedStudyingAt: string | null;
  userCardId: string;
};

/** A quiz answer's review, with the card rows it was recorded against. */
export type QuizAnswerReview = PersistedStudyCardReview & {
  fsrsCardId: string;
  studyCardId: string;
};

/**
 * Read a quiz card's rows, tombstones included: an answer revives a soft-deleted card, so a "before"
 * that hid it could not be restored. Pair a read taken before an answer with one taken after,
 * through {@link diffQuizEnrollment}.
 */
export async function readQuizCardState(actorDb: ActorDatabase, key: string): Promise<QuizCardState> {
  return actorDb(import.meta.location).transaction(async (tx) => {
    const ids = await resolveUserCardRowIds(tx, { key, kind: "quiz" });
    const overlay = await tx.queryRaw.userQuizCard.findFirst({
      columns: { deletedAt: true, id: true },
      where: { id: { eq: ids.detailId } },
    });
    // Read directly, not through the overlay's `userCard` relation: that relation hides
    // soft-deleted cards even on the raw handle.
    const card = await tx.queryRaw.userCard.findFirst({
      columns: {
        deferredUntil: true,
        deletedAt: true,
        fsrsCardId: true,
        queuedAt: true,
        reviewRechargeAfter: true,
        startedStudyingAt: true,
      },
      where: { id: { eq: ids.userCardId } },
    });
    return { card: card ?? null, overlay: overlay ?? null, userCardId: ids.userCardId };
  });
}

function isEnrolled(state: QuizCardState): state is QuizCardState & {
  card: QuizEnrollmentColumns & { fsrsCardId: string };
  overlay: { deletedAt: null; id: string };
} {
  return (
    state.overlay !== null &&
    state.overlay.deletedAt === null &&
    state.card !== null &&
    state.card.deletedAt === null &&
    state.card.fsrsCardId !== null
  );
}

/** The enrollment an answer made: in study after it, not before. `null` for a card already in study. */
export function diffQuizEnrollment(
  before: QuizCardState,
  after: QuizCardState,
): QuizEnrollmentChange | null {
  if (!isEnrolled(after) || isEnrolled(before)) {
    return null;
  }
  return {
    before:
      before.overlay === null
        ? null
        : { card: before.card, overlayDeletedAt: before.overlay.deletedAt },
    fsrsCardId: after.card.fsrsCardId,
    overlayId: after.overlay.id,
    startedStudyingAt: after.card.startedStudyingAt,
    userCardId: after.userCardId,
  };
}

/**
 * Record a quiz answer: correct reviews the card Easy, wrong reviews it Again. The first answer —
 * given in the lesson that teaches the quiz — also enrolls the card, and is its first review, so a
 * quiz answered correctly comes back the next day and one answered wrong within minutes.
 *
 * One transaction: the enrollment never lands without its review, or the reverse. Exactly-once
 * through `review.userCardReviewId` (see `persistStudyCardReviewInTransaction`): a retry of an
 * answer that landed finds its review row and records nothing new.
 */
export async function answerQuizCard({
  actorDb,
  correct,
  key,
  now,
  review: reviewIds,
}: {
  actorDb: ActorDatabase;
  correct: boolean;
  key: string;
  now: Date;
  review: { fsrsCardReviewLogId: string; userCardReviewId: string };
}): Promise<QuizAnswerReview> {
  const nowIso = now.toISOString();
  return actorDb(import.meta.location).transaction(async (tx) => {
    const { ids } = await ensureUserCardRows(tx, { key, kind: "quiz" }, nowIso);
    const card = await tx.query.userCard.findFirst({
      columns: { fsrsCardId: true },
      where: { id: { eq: ids.userCardId } },
    });
    if (card === undefined) {
      throw new Error(`Card rows for quiz ${key} were not written`);
    }
    let { fsrsCardId } = card;
    if (fsrsCardId === null) {
      // Due now: the review recorded below schedules it at once.
      await startUserCardSchedule(tx, {
        dueIso: nowIso,
        fsrsCardId: ids.fsrsCardId,
        nowIso,
        scheduledDays: 0,
      });
      await tx
        .update(actorSchema.userCard)
        .set({
          deferredUntil: null,
          fsrsCardId: ids.fsrsCardId,
          queuedAt: null,
          reviewRechargeAfter: null,
          startedStudyingAt: nowIso,
        })
        .where(eq(actorSchema.userCard.id, ids.userCardId))
        .run();
      fsrsCardId = ids.fsrsCardId;
    }
    const review = await persistStudyCardReviewInTransaction(tx, {
      ...reviewIds,
      fsrsCardId,
      now,
      rating: correct ? REVIEW_RATING.Easy : REVIEW_RATING.Again,
      studyCardId: ids.userCardId,
    });
    return { ...review, fsrsCardId, studyCardId: ids.userCardId };
  });
}

/**
 * Take a quiz's enrollment back out of study, returning its rows to exactly what they were before
 * the answer that enrolled it: a card the answer created is tombstoned with its overlay, and a card
 * it revived gets its previous columns back. The scheduler row is soft-deleted, since these tables
 * sync. Undo the answer's review first (`undoStudyCardReview`).
 *
 * Idempotent, and never clobbers a later change: a card no longer in study through this enrollment
 * (already undone, reset, re-enrolled) is left alone.
 */
export async function undoQuizEnrollment({
  actorDb,
  change,
  now,
}: {
  actorDb: ActorDatabase;
  change: QuizEnrollmentChange;
  now: Date;
}): Promise<void> {
  const nowIso = now.toISOString();
  await actorDb(import.meta.location).transaction(async (tx) => {
    const card = await tx.queryRaw.userCard.findFirst({
      columns: { deletedAt: true, fsrsCardId: true, startedStudyingAt: true },
      where: { id: { eq: change.userCardId } },
    });
    if (
      card?.fsrsCardId !== change.fsrsCardId ||
      card.startedStudyingAt !== change.startedStudyingAt ||
      card.deletedAt !== null
    ) {
      return;
    }
    await tx
      .update(actorSchema.fsrsCard)
      .set({ deletedAt: nowIso })
      .where(eq(actorSchema.fsrsCard.id, change.fsrsCardId))
      .run();
    await tx
      .update(actorSchema.userCard)
      .set(change.before?.card ?? { deletedAt: nowIso })
      .where(eq(actorSchema.userCard.id, change.userCardId))
      .run();
    await tx
      .update(actorSchema.userQuizCard)
      .set({ deletedAt: change.before === null ? nowIso : change.before.overlayDeletedAt })
      .where(eq(actorSchema.userQuizCard.id, change.overlayId))
      .run();
  });
}
